import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { LcncAppPicker } from './LcncAppPicker.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ScreenRow {
  readonly screenId: string;
  readonly screenCode: string;
  readonly screenName: string;
  readonly screenType: string;
  readonly status: string;
  readonly isResponsive: boolean;
}

const SCREEN_TYPES = ['', 'CREATE', 'EDIT', 'VIEW', 'APPROVAL', 'DASHBOARD', 'SEARCH', 'REPORT', 'LANDING'];
const STATUSES = ['', 'DRAFT', 'TESTING', 'PUBLISHED', 'ARCHIVED'];
const CREATE_TYPES = SCREEN_TYPES.filter(Boolean);

/**
 * M28 — Screen Designer (§10 / §32). Composes the eight responsive screen
 * archetypes (CREATE, EDIT, VIEW, APPROVAL, DASHBOARD, SEARCH, REPORT, LANDING)
 * over designed forms. Lists every screen_definition (T368) with its archetype,
 * lifecycle status and responsive flag, filterable by screen type and status.
 * Supports create → publish, wired to the guarded /v1/lcnc/screens write API.
 */
export function M28ScreenDesignerPage(): ReactNode {
  const { t: tr } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ScreenRow[]>([]);
  const [screenType, setScreenType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [applicationId, setApplicationId] = useState('');
  const [name, setName] = useState('');
  const [newType, setNewType] = useState('VIEW');
  const [responsive, setResponsive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (screenType) params.set('screenType', screenType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ScreenRow[] }>(`/v1/lcnc/screens${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, screenType, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m28.screenDesigner.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(tr('m28.screenDesigner.validationApplicationId')); return; }
    if (!name.trim()) { setActionError(tr('m28.screenDesigner.validationName')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ScreenRow }>(`/v1/lcnc/screens`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), screenName: name.trim(), screenType: newType, isResponsive: responsive },
        token,
      });
      setNotice(tr('m28.screenDesigner.noticeCreated', { code: r.data.screenCode }));
      setName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.screenDesigner.verbCreate'), 'SCREEN_DESIGN'));
    } finally {
      setBusy(false);
    }
  };

  const publish = async (s: ScreenRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/lcnc/screens/${s.screenId}/publish`, { method: 'POST', body: {}, token });
      setNotice(tr('m28.screenDesigner.noticePublished', { code: s.screenCode }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m28.screenDesigner.verbPublish'), 'SCREEN_DESIGN'));
    }
  };

  return (
    <main aria-label="screen-designer">
      <h1>{tr('m28.screenDesigner.title')}</h1>

      <section aria-label="create-screen">
        <h2>{tr('m28.screenDesigner.newHeading')}</h2>
        <form aria-label="screen-form" onSubmit={(e) => void create(e)}>
          <LcncAppPicker label={tr('m28.screenDesigner.fieldApplicationId')} value={applicationId} onPick={setApplicationId} />
          <label>{tr('m28.screenDesigner.fieldName')}<input aria-label="field-screen-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{tr('m28.screenDesigner.fieldType')}
            <select aria-label="field-screen-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m28.screenDesigner.fieldResponsive')}<input type="checkbox" aria-label="field-responsive" checked={responsive} onChange={(e) => setResponsive(e.target.checked)} /></label>
          <button type="submit" aria-label="submit-screen" disabled={busy}>{busy ? tr('m28.screenDesigner.creating') : tr('m28.screenDesigner.create')}</button>
        </form>
        {actionError && <p role="alert" aria-label="screen-error">{actionError}</p>}
        {notice && <p aria-label="screen-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m28.screenDesigner.filterType')}
          <select aria-label="filter-screen-type" value={screenType} onChange={(e) => setScreenType(e.target.value)}>
            {SCREEN_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m28.screenDesigner.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m28.screenDesigner.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m28.screenDesigner.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m28.screenDesigner.loading')}</p> : (
        <>
          <p aria-label="screen-count">{tr('m28.screenDesigner.count', { count: rows.length })}</p>
          <table aria-label="screen-table">
            <thead><tr><th>{tr('m28.screenDesigner.colCode')}</th><th>{tr('m28.screenDesigner.colName')}</th><th>{tr('m28.screenDesigner.colType')}</th><th>{tr('m28.screenDesigner.colStatus')}</th><th>{tr('m28.screenDesigner.colResponsive')}</th><th>{tr('m28.screenDesigner.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.screenId}>
                  <td>{s.screenCode}</td><td>{s.screenName}</td><td>{s.screenType}</td>
                  <td>{s.status}</td><td>{s.isResponsive ? tr('m28.screenDesigner.yes') : tr('m28.screenDesigner.no')}</td>
                  <td><button type="button" aria-label={`publish-${s.screenCode}`} onClick={() => void publish(s)}>{tr('m28.screenDesigner.btnPublish')}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
